import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { toast } from "sonner";

export interface MessageAttachment {
  id: string;
  message_id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size: number | null;
  created_at: string;
}

export interface ClientMessage {
  id: string;
  organization_id: string;
  client_id: string;
  sender_type: "organization" | "client";
  sender_user_id: string | null;
  message: string;
  read_at: string | null;
  created_at: string;
  attachments?: MessageAttachment[];
}

export function useClientMessages(clientId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: messages, isLoading } = useQuery({
    queryKey: ["client-messages", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      
      const { data, error } = await supabase
        .from("client_messages")
        .select(`
          *,
          attachments:message_attachments(*)
        `)
        .eq("client_id", clientId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as ClientMessage[];
    },
    enabled: !!clientId,
  });

  // Real-time subscription
  useEffect(() => {
    if (!clientId) return;

    const channel = supabase
      .channel(`client-messages-${clientId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "client_messages",
          filter: `client_id=eq.${clientId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["client-messages", clientId] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "client_messages",
          filter: `client_id=eq.${clientId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["client-messages", clientId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId, queryClient]);

  const sendMessageMutation = useMutation({
    mutationFn: async ({
      message,
      senderType,
      organizationId,
      files,
    }: {
      message: string;
      senderType: "organization" | "client";
      organizationId: string;
      files?: File[];
    }) => {
      if (!clientId) throw new Error("Client ID is required");

      const { data: { user } } = await supabase.auth.getUser();

      // Insert the message
      const { data: newMessage, error: messageError } = await supabase
        .from("client_messages")
        .insert({
          client_id: clientId,
          organization_id: organizationId,
          sender_type: senderType,
          sender_user_id: senderType === "organization" ? user?.id : null,
          message,
        })
        .select()
        .single();

      if (messageError) throw messageError;

      // Upload attachments if any
      if (files && files.length > 0) {
        for (const file of files) {
          const filePath = `${organizationId}/${clientId}/${newMessage.id}/${file.name}`;
          
          const { error: uploadError } = await supabase.storage
            .from("message-attachments")
            .upload(filePath, file);

          if (uploadError) {
            console.error("Upload error:", uploadError);
            continue;
          }

          const { data: urlData } = supabase.storage
            .from("message-attachments")
            .getPublicUrl(filePath);

          await supabase.from("message_attachments").insert({
            message_id: newMessage.id,
            file_name: file.name,
            file_url: urlData.publicUrl,
            file_type: file.type,
            file_size: file.size,
          });
        }
      }

      // Send email notification
      try {
        await supabase.functions.invoke("send-message-notification", {
          body: { messageId: newMessage.id },
        });
      } catch (emailError) {
        console.error("Email notification error:", emailError);
        // Don't throw - message was still sent successfully
      }

      return newMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-messages", clientId] });
      toast.success("Message sent!");
    },
    onError: (error: Error) => {
      toast.error("Failed to send message: " + error.message);
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (messageIds: string[]) => {
      const { error } = await supabase
        .from("client_messages")
        .update({ read_at: new Date().toISOString() })
        .in("id", messageIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-messages", clientId] });
    },
  });

  const unreadCount = messages?.filter(
    (m) => !m.read_at && m.sender_type !== "organization"
  ).length || 0;

  return {
    messages: messages || [],
    isLoading,
    sendMessage: sendMessageMutation.mutate,
    isSending: sendMessageMutation.isPending,
    markAsRead: markAsReadMutation.mutate,
    unreadCount,
  };
}

// Hook for organization to get all client conversations with unread counts
export function useClientConversations(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["client-conversations", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      // Get all messages for this organization
      const { data: allMessages, error: messagesError } = await supabase
        .from("client_messages")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (messagesError) throw messagesError;
      if (!allMessages || allMessages.length === 0) return [];

      // Get unique client IDs
      const clientIds = [...new Set(allMessages.map((m) => m.client_id))];

      // Fetch client info separately
      const { data: clients, error: clientsError } = await supabase
        .from("clients")
        .select("id, name, email")
        .in("id", clientIds);

      if (clientsError) throw clientsError;

      // Build a map of clients
      const clientsMap = new Map(clients?.map((c) => [c.id, c]) || []);

      // Group by client and count unread
      const clientMap = new Map<string, {
        clientId: string;
        clientName: string;
        clientEmail: string;
        unreadCount: number;
        lastMessageAt: string;
      }>();

      allMessages.forEach((msg) => {
        const existing = clientMap.get(msg.client_id);
        const clientInfo = clientsMap.get(msg.client_id);
        
        if (!existing) {
          clientMap.set(msg.client_id, {
            clientId: msg.client_id,
            clientName: clientInfo?.name || "Unknown",
            clientEmail: clientInfo?.email || "",
            unreadCount: msg.sender_type === "client" && !msg.read_at ? 1 : 0,
            lastMessageAt: msg.created_at,
          });
        } else if (msg.sender_type === "client" && !msg.read_at) {
          existing.unreadCount++;
        }
      });

      return Array.from(clientMap.values()).sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
      );
    },
    enabled: !!organizationId,
    refetchInterval: 5000, // Poll every 5 seconds as fallback
  });

  // Real-time subscription for organization-wide messages
  useEffect(() => {
    if (!organizationId) return;

    const channel = supabase
      .channel(`org-messages-${organizationId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "client_messages",
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          // Invalidate both conversations list and individual message threads
          queryClient.invalidateQueries({ queryKey: ["client-conversations", organizationId] });
          if (payload.new && typeof payload.new === 'object' && 'client_id' in payload.new) {
            queryClient.invalidateQueries({ queryKey: ["client-messages", payload.new.client_id] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organizationId, queryClient]);

  return query;
}
